import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import MapView from "./components/MapView.jsx";
import ParameterPicker from "./components/ParameterPicker.jsx";
import ScenarioPanel from "./components/ScenarioPanel.jsx";
import Timeline from "./components/Timeline.jsx";
import { createApi } from "./lib/api.js";
import { createAuth } from "./lib/auth.js";
import { resolveConfig } from "./lib/config.js";
import { saveBlob } from "./lib/download.js";
import { renderField, toDataUrl } from "./lib/gridCanvas.js";
import { loadDraft, loadHistory, pushHistory, saveDraft } from "./lib/history.js";
import { observationsAt } from "./lib/observations.js";
import { requestParameters, restoreSelection } from "./lib/parameters.js";
import { COLORSCALES, FADE_HIGH, FADE_LOW } from "./lib/palettes.js";
import { pageTitle, producerName, publishTarget, zipName } from "./lib/producer.js";
import { createAutoReissuer } from "./lib/autoReissue.js";
import { ALERT_MINUTES_DEFAULT, ALERT_MINUTES_MAX, ALERT_MINUTES_MIN, AUTO_REISSUE_MIN_MINUTES, anchorFromResponse, autoReissueAllowed, autoReissueBlockedReason, clampAlertMinutes, notStartedYet, reissueIntervalMs, retryWhenStartedMs } from "./lib/reissue.js";
import { newScenario, reducer, toIssueRequest, validate } from "./lib/scenario.js";

const CONFIG = resolveConfig(window.WX);
const AUTH = createAuth(CONFIG);
const API = createApi(CONFIG.apiBase, AUTH.fetch);
const CACHE_MAX = 64;

// What auto re-issue keeps alive: a manifest or a history entry.
const targetOf = (m) => ({ issueId: m.issue_id, name: m.name, minutes: m.alert_minutes });

export default function App() {
  const [boot, setBoot] = useState({ state: "loading", error: null });
  const [catalog, setCatalog] = useState(null);
  const draft = useMemo(() => loadDraft(), []);
  const [scenarios, dispatch] = useReducer(reducer, draft?.scenarios || []);
  const [horizon, setHorizon] = useState(draft?.horizon || 24);
  // The field's text as typed; the request uses it clamped.
  const [alertText, setAlertText] = useState(String(clampAlertMinutes(draft?.alertMinutes ?? ALERT_MINUTES_DEFAULT)));
  const alertMinutes = clampAlertMinutes(alertText);
  const [auto, setAuto] = useState({ target: null, anchorMs: null, stopped: null, waitingFor: null });
  const [reissueNote, setReissueNote] = useState(null);
  const [parameters, setParameters] = useState(() => restoreSelection(draft?.parameters));
  const [placing, setPlacing] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState(() => loadHistory());
  const [manifest, setManifest] = useState(null);
  const [hour, setHour] = useState(0);
  const [param, setParam] = useState("wind_speed_gust_surface");
  const [overlay, setOverlay] = useState(null);
  const [loadingField, setLoadingField] = useState(false);
  const [alerts, setAlerts] = useState(null);
  const [showAlerts, setShowAlerts] = useState(true);
  const [obs, setObs] = useState(null);
  const [showObs, setShowObs] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const cache = useRef(new Map());

  useEffect(() => {
    (async () => {
      try {
        await AUTH.init();
        const u = AUTH.user();
        if (u && !u.staff) throw new Error("This demo is for Shyft staff. Sign in with your staff account.");
        const c = await API.catalog();
        document.title = pageTitle(c);
        setCatalog(c);
        setBoot({ state: "ready", error: null });
      } catch (e) {
        setBoot({ state: "error", error: e.message || String(e) });
      }
    })();
  }, []);

  useEffect(() => saveDraft({ scenarios, horizon, parameters, alertMinutes }), [scenarios, horizon, parameters, alertMinutes]);

  const specByType = useMemo(() => Object.fromEntries((catalog?.disasters || []).map((d) => [d.type, d])), [catalog]);

  const place = useCallback((lat, lon) => {
    const spec = specByType[placing];
    if (!spec) return;
    const s = newScenario(spec, lat, lon);
    dispatch({ type: "add", scenario: s });
    setSelectedId(s.id);
    setPlacing(null);
  }, [placing, specByType]);

  const loadIssue = useCallback(async (m) => {
    setManifest(m);
    setHour(Math.min(hour, m.horizon_hours));
    const hl = m.highlight_parameters || [];
    setParam((p) => (hl.includes(p) ? p : hl[0] || "wind_speed_gust_surface"));
    setAlerts(null);
    setObs(null);
    try {
      const [a, o] = await Promise.all([API.alerts(m.issue_id), m.observations ? API.observations(m.issue_id) : null]);
      setAlerts(a);
      setObs(o);
    } catch (e) {
      setError(`Could not load alerts/observations: ${e.message}`);
    }
  }, [hour]);

  const issue = async () => {
    const problems = validate(scenarios, catalog, horizon, parameters);
    if (problems.length) {
      setError(problems.join(" "));
      return;
    }
    await post(toIssueRequest(scenarios, { horizon, alertMinutes, parameters: requestParameters(parameters, catalog) }));
  };

  // POST an issue; the manifest when it was listed, else null.
  const post = async (body) => {
    setBusy(true);
    setError(null);
    try {
      const m = await API.issue(body);
      // When its alerts were sent, from the server's own sent/server_time.
      anchors.current.set(m.issue_id, anchorFromResponse({ sent: m.alerts?.sent, server_time: m.server_time }, Date.now()));
      setHistory(pushHistory(m));
      await loadIssue(m);
      if (auto.target && auto.target.issueId !== m.issue_id) {
        // Auto re-issue follows an issue explicitly, never silently — and
        // never onto alerts too short to keep continuously live.
        if (!autoReissueAllowed(m.alert_minutes)) {
          setReissueNote(`Auto re-issue stays on "${auto.target.name}": the new issue's alerts live ${m.alert_minutes} min, under the ${AUTO_REISSUE_MIN_MINUTES} it needs.`);
        } else {
          const move = window.confirm(`Auto re-issue is keeping "${auto.target.name}" (${auto.target.issueId}) alive. Move it to the new issue "${m.name}"?`);
          if (move) autoRef.current.start(targetOf(m), anchors.current.get(m.issue_id));
        }
      }
      return m;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  // Alerts-only re-issue: fresh alerts that update the previous ones for the
  // scenarios that have started; no GRIB, no observations.
  const anchors = useRef(new Map()); // issue id → when its alerts were last sent (browser clock)
  const busyRef = useRef(false);
  busyRef.current = busy;
  const reissueAlerts = async (issueId) => {
    setBusy(true);
    setError(null);
    try {
      const r = await API.reissueAlerts(issueId);
      const anchorMs = anchorFromResponse(r, Date.now());
      anchors.current.set(issueId, anchorMs);
      setReissueNote(`Re-issued ${r.count} alert(s) of ${issueId} at ${r.sent.slice(11, 19)}Z, ending ${r.expires.slice(11, 19)}Z${r.skipped.length ? `; not started yet, skipped: ${r.skipped.join(", ")}` : ""}.`);
      if (manifestRef.current?.issue_id === issueId) setAlerts(await API.alerts(issueId));
      return { ok: true, anchorMs };
    } catch (e) {
      const retryInMs = e.status === 422 ? retryWhenStartedMs(e.detail) : null;
      if (retryInMs != null) {
        const at = e.detail.earliest_onset.slice(11, 16);
        setReissueNote(`Nothing of ${issueId} has started yet; the first scenario starts at ${at}Z.`);
        return { ok: false, retryInMs, waitingFor: `${at}Z` };
      }
      setError(`Re-issue failed: ${e.message}`);
      return { ok: false, error: e.message };
    } finally {
      setBusy(false);
    }
  };
  const reissueRef = useRef(reissueAlerts);
  reissueRef.current = reissueAlerts;
  const manifestRef = useRef(manifest);
  manifestRef.current = manifest;

  const autoRef = useRef(null);
  if (autoRef.current === null) {
    autoRef.current = createAutoReissuer({
      reissue: (id) => reissueRef.current(id),
      isBusy: () => busyRef.current,
      onChange: setAuto,
    });
  }
  useEffect(() => {
    const a = autoRef.current;
    const unbind = a.bindPagehide(window);
    return () => { unbind(); a.stop(); };
  }, []);

  const latest = history[0] || null;
  // Re-issue acts on the open issue, else the latest.
  const subject = manifest ? targetOf(manifest) : latest ? targetOf(latest) : null;
  const skippedNow = manifest && subject?.issueId === manifest.issue_id ? notStartedYet(manifest, Date.now()) : [];
  const reissueNow = async () => {
    if (!subject) return;
    const r = await reissueAlerts(subject.issueId);
    if (r.ok) autoRef.current.note(subject.issueId, r.anchorMs);
  };
  const toggleAuto = (on) => {
    if (!on) autoRef.current.stop();
    else if (subject) autoRef.current.start(subject, anchors.current.get(subject.issueId) ?? null);
  };

  const openHistory = async (id) => {
    setError(null);
    try {
      await loadIssue(await API.getIssue(id));
    } catch (e) {
      setError(e.status === 404 ? `Issue ${id} has expired on the server.` : e.message);
    }
  };

  // Field preview: fetch, render, cache; prefetch the next hour.
  const fetchOverlay = useCallback(async (m, p, h) => {
    const key = `${m.issue_id}|${p}|${h}`;
    if (cache.current.has(key)) return cache.current.get(key);
    const spec = m.parameters.find((x) => x.name === p);
    const field = await API.field(m.issue_id, p, h);
    if (!field.nj) {
      // Nothing touched this parameter this hour: draw nothing.
      cache.current.set(key, null);
      return null;
    }
    const cs = COLORSCALES[spec.colorscale] || COLORSCALES.wind;
    const fade = FADE_LOW.has(spec.colorscale) ? "low" : FADE_HIGH.has(spec.colorscale) ? "high" : null;
    const img = renderField(field, cs.stops, spec.display_range, { fade, height: Math.min(field.nj * 2, 900) });
    const ov = { url: toDataUrl(img), bounds: img.bounds };
    cache.current.set(key, ov);
    if (cache.current.size > CACHE_MAX) cache.current.delete(cache.current.keys().next().value);
    return ov;
  }, []);

  useEffect(() => {
    if (!manifest) {
      setOverlay(null);
      return;
    }
    let live = true;
    setLoadingField(true);
    fetchOverlay(manifest, param, hour)
      .then((ov) => live && setOverlay(ov))
      .catch((e) => live && setError(`Preview failed: ${e.message}`))
      .finally(() => live && setLoadingField(false));
    if (hour < manifest.horizon_hours) fetchOverlay(manifest, param, hour + 1).catch(() => {});
    return () => { live = false; };
  }, [manifest, param, hour, fetchOverlay]);

  useEffect(() => {
    if (!playing || !manifest) return;
    const t = setInterval(() => setHour((h) => (h >= manifest.horizon_hours ? 0 : h + 1)), 700);
    return () => clearInterval(t);
  }, [playing, manifest]);

  const visibleObs = useMemo(() => (showObs && manifest ? observationsAt(obs, manifest, hour) : null), [obs, manifest, hour, showObs]);

  const download = async () => {
    setDownloading(true);
    try {
      await saveBlob(await API.downloadBlob(manifest.issue_id), zipName(manifest));
    } catch (e) {
      setError(`Download failed: ${e.message}`);
    } finally {
      setDownloading(false);
    }
  };

  if (boot.state !== "ready") {
    return (
      <div className="chaos-boot">
        <img src="./assets/wxchange.png" alt="wxchange" className="chaos-logo" />
        {boot.state === "loading" ? <p>Loading…</p> : <p className="chaos-error">{boot.error}</p>}
      </div>
    );
  }

  const user = AUTH.user();
  return (
    <div className="chaos-app">
      <aside className="chaos-side">
        <header className="chaos-brand">
          <img src="./assets/wxchange.png" alt="wxchange" className="chaos-logo" />
          <div>
            <strong>{producerName(catalog)}</strong>
            <small>forecaster console</small>
          </div>
        </header>

        <ScenarioPanel
          catalog={catalog}
          scenarios={scenarios}
          selectedId={selectedId}
          placing={placing}
          horizon={horizon}
          onPick={setPlacing}
          onSelect={setSelectedId}
          onParam={(id, key, value) => dispatch({ type: "param", id, key, value })}
          onRemove={(id) => dispatch({ type: "remove", id })}
        />

        <section className="chaos-section">
          <div className="chaos-section-head"><h3>Model run</h3></div>
          <label className="chaos-param">
            <span>Forecast horizon <em>{horizon} h</em></span>
            <input className="wxr" type="range" min={1} max={catalog.defaults.max_horizon_hours} value={horizon} onChange={(e) => setHorizon(Number(e.target.value))} />
          </label>
          <label className="chaos-param">
            <span>Alerts end after (minutes) <em>{alertMinutes} min</em></span>
            <input className="input" type="number" min={ALERT_MINUTES_MIN} max={ALERT_MINUTES_MAX} step={1} value={alertText} onChange={(e) => setAlertText(e.target.value)} onBlur={() => setAlertText(String(alertMinutes))} />
          </label>
          <ParameterPicker catalog={catalog} selection={parameters} onChange={setParameters} />
          <div className="chaos-actions">
            <button type="button" className="btn btn-primary" onClick={issue} disabled={busy || !scenarios.length}>
              {busy ? "Issuing…" : "Issue"}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { dispatch({ type: "clear" }); setSelectedId(null); }} disabled={!scenarios.length}>Clear</button>
          </div>
          <p className="muted chaos-hint">Issue writes one GRIB2 file per forecast hour, plus observation and alert GeoJSON. {publishTarget(catalog)}</p>
          {error && <p className="chaos-error" role="alert">{error}</p>}
        </section>

        {history.length > 0 && (
          <section className="chaos-section">
            <div className="chaos-section-head"><h3>Recent issues</h3></div>
            <div className="chaos-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={reissueNow} disabled={busy || !subject} title={subject ? `Re-issue the alerts of "${subject.name}" (${subject.issueId}) so they stay live` : ""}>
                Re-issue alerts
              </button>
              <label className="chaos-toggle">
                <input type="checkbox" checked={!!auto.target} onChange={(e) => toggleAuto(e.target.checked)} disabled={!auto.target && (!subject || !autoReissueAllowed(subject.minutes))} />
                <span>
                  {auto.target
                    ? auto.waitingFor
                      ? `Auto re-issue for "${auto.target.name}" (${auto.target.issueId}) waiting for ${auto.waitingFor}, when its first scenario starts`
                      : `Auto re-issue keeping "${auto.target.name}" (${auto.target.issueId}) alive, every ${reissueIntervalMs(auto.target.minutes) / 60_000} min`
                    : `Auto re-issue "${subject?.name ?? ""}" every ${reissueIntervalMs(subject?.minutes) / 60_000} min`}
                </span>
              </label>
            </div>
            {!auto.target && subject && !autoReissueAllowed(subject.minutes) && <p className="muted chaos-hint">{autoReissueBlockedReason(subject.minutes)}</p>}
            {skippedNow.length > 0 && <p className="muted chaos-hint">Not started yet, so not re-issued until they start: {skippedNow.join(", ")}.</p>}
            {reissueNote && <p className="muted chaos-hint">{reissueNote}</p>}
            {auto.stopped && !auto.target && <p className="chaos-error" role="status">Auto re-issue stopped: {auto.stopped}.</p>}
            <ul className="chaos-history">
              {history.map((h) => (
                <li key={h.issue_id}>
                  <button type="button" className={manifest?.issue_id === h.issue_id ? "on" : ""} onClick={() => openHistory(h.issue_id)}>
                    <span>{h.name}</span>
                    <small>{h.issue_id} · {h.horizon_hours} h</small>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        <footer className="chaos-user muted">
          {user?.name || "Signed in"}
          {AUTH.mode === "keycloak" && <button type="button" className="btn btn-ghost btn-xs" onClick={() => AUTH.logout()}>Sign out</button>}
        </footer>
      </aside>

      <main className="chaos-main">
        <MapView
          config={CONFIG}
          scenarios={scenarios}
          selectedId={selectedId}
          placing={placing}
          horizon={horizon}
          overlay={manifest ? overlay : null}
          alerts={showAlerts ? alerts : null}
          observations={visibleObs}
          onPlace={place}
          onMove={(id, lat, lon) => dispatch({ type: "move", id, lat, lon })}
          onSelect={setSelectedId}
        />
        {placing && <div className="chaos-banner">Click the map to drop a {specByType[placing]?.label.toLowerCase()}. <button type="button" className="btn btn-ghost btn-xs" onClick={() => setPlacing(null)}>Cancel</button></div>}
        {manifest && (
          <Timeline
            manifest={manifest}
            hour={hour}
            param={param}
            playing={playing}
            showAlerts={showAlerts}
            showObs={showObs}
            loading={loadingField}
            downloading={downloading}
            onHour={setHour}
            onParam={setParam}
            onPlay={() => setPlaying((p) => !p)}
            onShowAlerts={setShowAlerts}
            onShowObs={setShowObs}
            onDownload={download}
          />
        )}
      </main>
    </div>
  );
}
